import { MetricValue, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { WidgetState } from "@nexus/widget-sdk";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { firstError, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { Carousel } from "@nexus/widget-kit";
import { EVENT_RESTREAMING_SERVICES_CONTRACT } from "./types";
import type { EventRestreamingService, EventRestreamingServicesData } from "./types";

export interface EventRestreamingServicesConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "event-restreaming-services",
  name: "Restreaming pirate services",
  description: "One protected event's restreaming pirate services, ranked by audience.",
  version: "1.0.0",
  dataRequests: {
    services: {
      source: "protection-overview-event-restreaming-services",
      contract: EVENT_RESTREAMING_SERVICES_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: EventRestreamingServicesConfig = {};

const numberFormat = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const percentFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

function ServiceCard({ service, rank }: { service: EventRestreamingService; rank: number }) {
  const safePct = Math.min(1, Math.max(0, service.safeIpPct));

  return (
    <div className="flex w-72 shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-primary">{service.label}</span>
        <span className="shrink-0 rounded-full bg-surface-elevated px-2 py-0.5 text-2xs font-medium text-muted">
          #{rank}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-2xs text-muted">
          <span className="flex items-center gap-1">
            <ShieldAlert className="h-3.5 w-3.5 text-status-error" aria-hidden />
            {numberFormat.format(service.distinctIps)} IPs
          </span>
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-status-neutral" aria-hidden />
            {percentFormat.format(safePct)} safe
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-status-error/20">
          <div
            className="h-full rounded-full bg-status-neutral"
            style={{ width: `${safePct * 100}%` }}
          />
        </div>
      </div>

      <div className="flex items-end justify-between border-t border-border pt-3">
        <div className="flex flex-col">
          <span className="text-metric-detail font-medium text-muted">Est. audience</span>
          <MetricValue size="compact" title={service.estimatedAudience.toLocaleString()}>
            {numberFormat.format(service.estimatedAudience)}
          </MetricValue>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-metric-detail font-medium text-muted">Reduction</span>
          <span className="text-sm font-medium tabular-nums text-status-neutral">
            {percentFormat.format(Math.min(1, Math.max(0, service.audienceReductionPct)))}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-metric-detail font-medium text-muted">Approx.</span>
          <span className="text-sm font-medium tabular-nums text-secondary">
            {numberFormat.format(service.approxAudience)}
          </span>
        </div>
      </div>
    </div>
  );
}

export function Component({ data, config }: WidgetProps<EventRestreamingServicesConfig>) {
  const error = firstError(data, ["services"]);
  const result = unwrap<EventRestreamingServicesData>(data.services);
  const loading = data.services?.isLoading && !result;
  const heading =
    config.title ??
    (result?.event ? `Restreaming pirate services – ${result.event.label}` : manifest.name);

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="cards" />
      ) : result.services.length === 0 ? (
        <WidgetState title="No restreaming pirate services detected for this event." />
      ) : (
        <div className="p-4">
          <Carousel>
            {result.services.map((service, index) => (
              <ServiceCard key={service.id} service={service} rank={index + 1} />
            ))}
          </Carousel>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<EventRestreamingServicesConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
